/**
 * "Update schedule" for a late job: what moves on its line (preview), then apply —
 * the moves are made, logged and the roster refilled for the days that changed.
 */
import { Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";

import jobOutcomesApi, { RescheduleProposal } from "../../../../../../services/job-outcomes-api";
import { waitForFill } from "../../controller/use-fill-range";
import { eyebrowSx, lbb } from "../../utils/tokens";

const when = (iso?: string | null) => (iso ? format(parseISO(iso), "EEE d MMM HH:mm") : "—");
const KIND_LABEL = { continuation: "Rest of the batch", rerun: "Runs again", pushed: "Moves on" } as const;

const actionSx: React.CSSProperties = {
	border: `1px solid ${lbb.navy20}`,
	background: lbb.paper,
	borderRadius: 4,
	padding: "5px 12px",
	fontFamily: lbb.fontUi,
	fontSize: 13,
	fontWeight: 600,
	color: lbb.navy,
	cursor: "pointer",
};

export const RescheduleDialog: React.FC<{
	workOrderId: number;
	onClose: () => void;
	/** The line moved and the roster refilled (or the refill could not be followed). */
	onApplied: () => void;
}> = ({ workOrderId, onClose, onApplied }) => {
	const [proposal, setProposal] = useState<RescheduleProposal | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [stage, setStage] = useState<"preview" | "applying" | "filling">("preview");
	const [fill, setFill] = useState<{ done: number; total: number } | null>(null);

	useEffect(() => {
		let mounted = true;
		jobOutcomesApi
			.propose(workOrderId)
			.then((p) => mounted && setProposal(p))
			.catch((err) => mounted && setError(err?.response?.data?.message ?? "Could not work out the update."));
		return () => {
			mounted = false;
		};
	}, [workOrderId]);

	const apply = useCallback(async () => {
		setStage("applying");
		setError(null);
		try {
			const result = await jobOutcomesApi.apply(workOrderId);
			if (result.fillJobId) {
				setStage("filling");
				await waitForFill(result.fillJobId, (p) => setFill({ done: p.done, total: p.total }));
			}
			onApplied();
		} catch (err) {
			setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "The schedule was not updated.");
			setStage("preview");
		}
	}, [workOrderId, onApplied]);
	const handleApply = useCallback(() => void apply(), [apply]);

	const busy = stage !== "preview";
	return (
		<Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy, pb: 0.5 }}>
				Update schedule{proposal ? ` · ${proposal.workstation}` : ""}
				{proposal && (
					<div style={{ fontFamily: lbb.fontUi, fontSize: 13, fontWeight: 400, color: lbb.ink60, marginTop: 2 }}>
						{proposal.label} was {proposal.outcome === "partial" ? "partly done" : "not completed"}. The work still owed goes next on the line, and the
						batches after it move on only as far as they must. Other lines stay as they are.
					</div>
				)}
			</DialogTitle>
			<DialogContent>
				{!proposal && !error && <LinearProgress sx={{ my: 2 }} />}
				{proposal && (
					<div style={{ overflowX: "auto" }}>
						<table style={{ borderCollapse: "collapse", width: "100%", fontFamily: lbb.fontUi, fontSize: 13 }}>
							<thead>
								<tr>
									{["", "Batch", "Was", "Now", ""].map((h, i) => (
										<th key={i} style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60, textAlign: "left", padding: "6px 8px", borderBottom: lbb.borderInk }}>
											{h}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{proposal.moves.map((m) => (
									<tr key={`${m.kind}-${m.workOrderId}`}>
										<td style={{ padding: "6px 8px", borderBottom: lbb.borderHair, color: m.kind === "pushed" ? lbb.ink60 : lbb.brickDeep, whiteSpace: "nowrap" }}>{KIND_LABEL[m.kind]}</td>
										<td style={{ padding: "6px 8px", borderBottom: lbb.borderHair, color: lbb.navy, fontWeight: 600 }}>{m.label}</td>
										<td style={{ padding: "6px 8px", borderBottom: lbb.borderHair, fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink60, whiteSpace: "nowrap" }}>
											{m.fromStart ? `${when(m.fromStart)} – ${format(parseISO(m.fromEnd ?? m.fromStart), "HH:mm")}` : "new"}
										</td>
										<td style={{ padding: "6px 8px", borderBottom: lbb.borderHair, fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink, whiteSpace: "nowrap" }}>
											{when(m.toStart)} – {format(parseISO(m.toEnd), "HH:mm")}
										</td>
										<td style={{ padding: "6px 8px", borderBottom: lbb.borderHair, whiteSpace: "nowrap" }}>
											{m.overtimeMinutes > 0 && (
												<span style={{ fontSize: 11, fontWeight: 700, color: lbb.warning, border: `1px solid ${lbb.warning}`, borderRadius: 3, padding: "0 5px" }}>
													+{(m.overtimeMinutes / 60).toFixed(1).replace(/\.0$/, "")} h OT
												</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<p style={{ fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink60, margin: "12px 0 0" }}>
							The line catches up {when(proposal.caughtUpAt)}. The roster is refilled for the days that change
							{proposal.manualPlacements > 0 ? `; ${proposal.manualPlacements} ${proposal.manualPlacements === 1 ? "person" : "people"} placed by hand on these batches come off and are placed again` : ""}. You can undo
							the update from the job card.
						</p>
					</div>
				)}
				{stage === "filling" && (
					<div style={{ marginTop: 12 }}>
						<LinearProgress variant={fill ? "determinate" : "indeterminate"} value={fill && fill.total ? (fill.done / fill.total) * 100 : 0} />
						<span style={{ fontFamily: lbb.fontUi, fontSize: 12.5, color: lbb.ink60 }}>Refilling the roster{fill ? ` — ${fill.done} of ${fill.total} plant-days` : "…"}</span>
					</div>
				)}
				{error && <p style={{ fontFamily: lbb.fontUi, fontSize: 13, color: lbb.brickDeep }}>{error}</p>}
			</DialogContent>
			<DialogActions>
				<button type="button" style={actionSx} onClick={onClose} disabled={busy}>
					Cancel
				</button>
				<button
					type="button"
					onClick={handleApply}
					disabled={!proposal || busy}
					style={{ ...actionSx, background: lbb.brick, borderColor: lbb.brick, color: lbb.paper, opacity: !proposal || busy ? 0.6 : 1 }}
				>
					{stage === "applying" ? "Updating…" : stage === "filling" ? "Refilling…" : `Update ${proposal ? proposal.moves.length : ""} ${proposal?.moves.length === 1 ? "batch" : "batches"}`}
				</button>
			</DialogActions>
		</Dialog>
	);
};
